import React, { useState, useEffect, useRef } from 'react';
import { useTaskPopup } from '@/contexts/TaskPopupContext';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { ChevronsUpDown } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useGrowthSquad } from '@/hooks/useGrowthSquad';
import { useSprints } from '@/hooks/useSprints';
import { useProjects } from '@/hooks/useProjects';
import { supabase } from '@/integrations/supabase/client';
import { useBrandManager } from '@/hooks/useBrandManager';
import { Calendar, User, FileText, Zap, MessageSquare, Copy, CalendarIcon, Image, ChevronLeft, ChevronRight, Plus, MessagesSquare, MoreVertical, Edit3, ImagePlus, Trash2, Tag, Paperclip, Flag, Target, Download, Users, Save, Check, Link2, Lightbulb, ClipboardList, Eye } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CommentsDialog } from '@/components/tasks/CommentsDialog';
import { AddCommentDialog } from '@/components/tasks/AddCommentDialog';
import { useTaskComments } from '@/hooks/useTaskComments';
import { format, differenceInDays } from 'date-fns';
import { cn } from '@/lib/utils';
import useEmblaCarousel from 'embla-carousel-react';
import { generateTaskUrl, generateTechnicalTaskUrl, generateSlug } from '@/utils/urlUtils';
import { makeLinksClickable } from '@/utils/linkUtils';
import { TimeTracker } from '@/components/ui/time-tracker';
import { logTimeEntry, TimeTrackingSource } from '@/hooks/useTimeTracking';
import { PhaseSelector } from '@/components/scale-2y3x/PhaseSelector';
import { UserHoverCard } from '@/components/shared/UserHoverCard';
import { DueDateTasksHoverCard } from './DueDateTasksHoverCard';
import { useTaskTerminology } from '@/hooks/useTaskTerminology';
import { TagSelector } from './TagSelector';
import { SubtasksTab } from './SubtasksTab';
import { TimeHistoryTab } from './TimeHistoryTab';
import { useSubtasks } from '@/hooks/useSubtasks';
import { useTaskCreationSettings } from '@/hooks/useTaskCreationSettings';
import { useUserGoogleCalendar } from '@/hooks/useUserGoogleCalendar';
// Searchable inline dropdown for Assigned To in TaskPopup
function AssignedToInlineSelector({ members, value, onValueChange, disabled, isUpdating }: {
  members: { id: string; name: string; alias?: string }[];
  value: string;
  onValueChange: (v: string) => void;
  disabled?: boolean;
  isUpdating?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={cn(
          "flex h-8 items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background w-[140px]",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50"
        )}
      >
        <span className="text-muted-foreground">{isUpdating ? 'Updating...' : 'Change'}</span>
        <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 z-[9999] mt-1 rounded-md border bg-popover shadow-md min-w-[220px]">
          <Command>
            <CommandInput placeholder="Search members..." autoFocus />
            <CommandList className="max-h-[200px]">
              <CommandEmpty>No members found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="__unassigned__"
                  onSelect={() => { onValueChange('unassigned'); setOpen(false); }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === 'unassigned' ? "opacity-100" : "opacity-0")} />
                  <div className="w-6 h-6 rounded-full bg-muted text-muted-foreground text-xs flex items-center justify-center mr-2">?</div>
                  <span>Unassigned</span>
                </CommandItem>
                {[...members].sort((a, b) => {
                  const lastNameA = a.name.trim().split(/\s+/).slice(-1)[0] || '';
                  const lastNameB = b.name.trim().split(/\s+/).slice(-1)[0] || '';
                  return lastNameA.localeCompare(lastNameB) || a.name.localeCompare(b.name);
                }).map((member) => (
                  <CommandItem
                    key={member.id}
                    value={member.name}
                    onSelect={() => { onValueChange(member.id); setOpen(false); }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", value === member.id ? "opacity-100" : "opacity-0")} />
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center mr-2">
                      {member.alias?.slice(0, 2).toUpperCase() || 'U'}
                    </div>
                    <span className="truncate">{member.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}

// Searchable inline dropdown for Project in TaskPopup
function ProjectInlineSelector({ projects, value, onValueChange, disabled, isUpdating }: {
  projects: { id: string; name: string; icon?: string }[];
  value: string;
  onValueChange: (v: string) => void;
  disabled?: boolean;
  isUpdating?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={cn(
          "flex h-8 items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background w-[140px]",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50"
        )}
      >
        <span className="text-muted-foreground">{isUpdating ? 'Updating...' : 'Change'}</span>
        <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
      </button>
      {open && (
        <div className="absolute top-full left-0 z-[9999] mt-1 rounded-md border bg-popover shadow-md w-[340px]">
          <Command>
            <CommandInput placeholder="Search projects..." autoFocus />
            <CommandList className="max-h-[200px]">
              <CommandEmpty>No projects found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="__none__"
                  onSelect={() => { onValueChange('none'); setOpen(false); }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === 'none' ? "opacity-100" : "opacity-0")} />
                  <span className="text-muted-foreground">No project</span>
                </CommandItem>
                {projects.map((project) => {
                  const ProjectIcon = project.icon ? (LucideIcons as any)[project.icon] : LucideIcons.Folder;
                  return (
                    <CommandItem
                      key={project.id}
                      value={project.name}
                      onSelect={() => { onValueChange(project.id); setOpen(false); }}
                    >
                      <Check className={cn("mr-2 h-4 w-4", value === project.id ? "opacity-100" : "opacity-0")} />
                      {ProjectIcon && <ProjectIcon className="h-4 w-4 mr-2" />}
                      <span className="truncate">{project.name}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}

const ImageCarousel = ({
  images,
  onDelete
}: {
  images: any[];
  onDelete?: (index: number) => void;
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCurrentIndex(emblaApi.selectedScrollSnap());
    };
    emblaApi.on('select', onSelect);
    onSelect();
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);
  return <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {images.map((image, index) => <div key={index} className="flex-[0_0_100%] min-w-0">
              <div className="flex flex-col items-center space-y-2 p-4 relative">
                <img src={typeof image === 'string' ? image : image.url || image.path || ''} alt={`Task image ${index + 1}`} className="max-w-full max-h-[60vh] object-contain rounded-lg border shadow-md" onError={e => {
              e.currentTarget.src = '/placeholder.svg';
            }} />
                {onDelete && (
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={() => onDelete(index)}
                    className="absolute top-6 left-6 h-8 w-8 p-0 bg-red-500 hover:bg-red-600 text-white shadow-lg z-20"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
                {typeof image === 'object' && (image.name || image.caption) && <p className="text-sm text-muted-foreground text-center absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/70 text-white px-2 py-1 rounded max-w-[80%]">
                    {image.name || image.caption}
                  </p>}
              </div>
            </div>)}
        </div>
      </div>

      {images.length > 1 && <>
          <Button variant="outline" size="sm" onClick={scrollPrev} className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 p-0 bg-white/90 hover:bg-white">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <Button variant="outline" size="sm" onClick={scrollNext} className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 p-0 bg-white/90 hover:bg-white">
            <ChevronRight className="h-4 w-4" />
          </Button>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-1">
            {images.map((_, index) => <div key={index} className={cn("w-2 h-2 rounded-full transition-colors", index === currentIndex ? "bg-primary" : "bg-muted")} />)}
          </div>

          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-2 py-1 rounded text-sm">
            {currentIndex + 1} / {images.length}
          </div>
        </>}
    </div>;
};
interface TaskData {
  id: string;
  title?: string;
  name?: string;
  description?: string;
  status: string;
  task_status?: string;
  priority?: string;
  difficulty?: string;
  source_type?: string;
  phase_id?: string;
  project_id?: string;
  created_at?: string;
  updated_at?: string;
  comments?: string;
  remarks?: string;
  assigned_to?: string;
  owner?: string; // For growth tasks
  submitted_by?: string;
  brand_id?: string;
  sprint_id?: string;
  due_date?: string;
  images?: any[];
  attachments?: any[];
  slug?: string;
  user_id?: string;
  watchers?: string[];
  internal?: boolean;
  brands?: {
    name: string;
    slug: string;
  };
  sprint?: {
    id: string;
    name: string;
    start_date?: string;
    end_date?: string;
  };
  growth_sprints?: {
    id: string;
    name: string;
    start_date?: string;
    end_date?: string;
  };
}
interface TaskPopupProps {
  isOpen: boolean;
  onClose: () => void;
  task: TaskData | null;
  taskType: 'foundational' | 'growth' | 'recurring';
  onStatusUpdate?: (taskId: string, status: string) => void;
  onCommentsUpdate?: (taskId: string, comments: string) => void;
  onRefetch?: () => void; // Add refetch callback
}
export const TaskPopup = ({
  isOpen,
  onClose,
  task,
  taskType,
  onStatusUpdate,
  onCommentsUpdate,
  onRefetch
}: TaskPopupProps) => {
  const { updateSelectedTask } = useTaskPopup();
  const { getTaskTypeName, getSourceTypeName } = useTaskTerminology();
  const queryClient = useQueryClient();
  const [comments, setComments] = useState('');
  const [remarks, setRemarks] = useState('');
  const [timeSpent, setTimeSpent] = useState(0);
  const [previousTimeSpent, setPreviousTimeSpent] = useState(0);
  const [timeSource, setTimeSource] = useState<TimeTrackingSource>('manual');
  const [dueDate, setDueDate] = useState<Date>();
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isUpdatingComments, setIsUpdatingComments] = useState(false);
  const [isUpdatingRemarks, setIsUpdatingRemarks] = useState(false);
  const [isUpdatingAssignment, setIsUpdatingAssignment] = useState(false);
  const [isUpdatingDueDate, setIsUpdatingDueDate] = useState(false);
  const [isUpdatingTimeSpent, setIsUpdatingTimeSpent] = useState(false);
  const [isUpdatingWatchers, setIsUpdatingWatchers] = useState(false);
  const [isUpdatingSprint, setIsUpdatingSprint] = useState(false);
  const [assignedSquadMember, setAssignedSquadMember] = useState<any>(null);
  const [ownerSquadMember, setOwnerSquadMember] = useState<any>(null);
  const [currentAssigneeId, setCurrentAssigneeId] = useState<string | 'unassigned'>('unassigned');
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [showCommentsDialog, setShowCommentsDialog] = useState(false);
  const [showAddCommentDialog, setShowAddCommentDialog] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [localImages, setLocalImages] = useState<any[]>([]);
  const [localAttachments, setLocalAttachments] = useState<any[]>([]);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [watchers, setWatchers] = useState<string[]>([]);
  const [phaseId, setPhaseId] = useState<string | undefined>(task?.phase_id);
  const [isUpdatingPhase, setIsUpdatingPhase] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(task?.project_id || null);
  const [isUpdatingProject, setIsUpdatingProject] = useState(false);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [showTagsDialog, setShowTagsDialog] = useState(false);
  const [allBrandTags, setAllBrandTags] = useState<Array<{id: string; name: string; color: string}>>([]);
  const [docHubViewContent, setDocHubViewContent] = useState<{title: string; content: string; type: string} | null>(null);
  const { subtasks } = useSubtasks(task?.id);
  const { projects: taskBrandProjects } = useProjects(task?.brand_id);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const attachmentInputRef = React.useRef<HTMLInputElement>(null);
  const {
    toast
  } = useToast();
  const {
    squadMembers
  } = useGrowthSquad();
  const {
    sprints
  } = useSprints();
  const { currentBrandId, selectBrand } = useBrandManager();
  const [taskBrandSquadMembers, setTaskBrandSquadMembers] = useState<any[]>([]);
  const [taskBrandSprints, setTaskBrandSprints] = useState<any[]>([]);
  const { settings: currentBrandSettings } = useTaskCreationSettings();
  
  // Fetch task-specific brand settings when task brand differs from current brand
  const taskBrandId = task?.brand_id;
  const { data: taskBrandSettings } = useQuery({
    queryKey: ['task-creation-settings', taskBrandId],
    queryFn: async () => {
      if (!taskBrandId) return null;
      const { data } = await supabase
        .from('task_creation_settings')
        .select('*')
        .eq('brand_id', taskBrandId)
        .maybeSingle();
      return data || null;
    },
    enabled: !!taskBrandId && taskBrandId !== currentBrandId,
  });
  const taskCreationSettings = (taskBrandId && taskBrandId !== currentBrandId) ? taskBrandSettings : currentBrandSettings;
  const { updateEvent: updateCalendarEvent } = useUserGoogleCalendar();
  const {
    comments: taskComments
  } = useTaskComments(task?.id || '', taskType, task?.brand_id);
  
  // Initialize task state when task changes
  useEffect(() => {
    if (task) {
      setComments(task.comments || '');
      setRemarks(task.remarks || '');
      const initialTime = (task as any).time_spent || 0;
      setTimeSpent(initialTime);
      setPreviousTimeSpent(initialTime);
      setWatchers(task.watchers || []);
      setTagIds((task as any).tag_ids || []);
      // Set due date from task data
      if (task.due_date) {
        setDueDate(new Date(task.due_date));
      } else {
        setDueDate(undefined);
      }
      // Fetch assigned squad member if task has assigned_to
      // For growth tasks, prefer assigned_to (new column) over owner (legacy)
      const assignedId = task.assigned_to || (taskType === 'growth' ? (task as any).owner : null);
      const initialAssignee = assignedId || 'unassigned';
      setCurrentAssigneeId(initialAssignee as string | 'unassigned');
      // Reset squad member immediately to avoid stale data display
      setAssignedSquadMember(null);
      if (assignedId) {
        fetchAssignedSquadMember(assignedId);
      }

      // Fetch owner squad member - for growth tasks use owner field (growth_squad.id), fallback to submitted_by, for others use user_id
      const ownerId = taskType === 'growth' ? (task.owner || task.submitted_by) : task.user_id;
      if (ownerId) {
        if (taskType === 'growth') {
          // For growth tasks, owner is the squad member id
          // Pass true to skip setting assignedSquadMember (we only want ownerSquadMember)
          fetchAssignedSquadMember(ownerId, true).then(member => setOwnerSquadMember(member));
        } else {
          fetchOwnerSquadMember(ownerId, task.brand_id);
        }
      } else {
        setOwnerSquadMember(null);
      }
      
      // Set edited description and title for editing
      setEditedDescription(task.description || '');
      setEditedTitle(taskTitle);
      
      // Set phase_id for 2y3x tasks
      setPhaseId(task.phase_id);
      
      // Set project_id
      setCurrentProjectId(task.project_id || null);
    }
  }, [task]);

  // Load squad members for the brand of the task (not just current brand)
  useEffect(() => {
    const loadTaskBrandSquad = async () => {
      if (!task?.brand_id) {
        setTaskBrandSquadMembers([]);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('growth_squad')
          .select('*')
          .eq('brand_id', task.brand_id);
        if (!error && data) {
          setTaskBrandSquadMembers(data);
        }
      } catch (error) {
        console.error('Error loading squad members for task brand:', error);
        setTaskBrandSquadMembers([]);
      }
    };

    const loadTaskBrandSprints = async () => {
      if (!task?.brand_id) {
        setTaskBrandSprints([]);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('growth_sprints')
          .select('*')
          .eq('brand_id', task.brand_id)
          .order('start_date', { ascending: true });
        if (!error && data) {
          setTaskBrandSprints(data);
        }
      } catch (error) {
        console.error('Error loading sprints for task brand:', error);
        setTaskBrandSprints([]);
      }
    };

    loadTaskBrandSquad();
    loadTaskBrandSprints();
  }, [task?.brand_id]);

  // Find current sprint for date validation
  const currentSprint = sprints.find(sprint => {
    const now = new Date();
    const startDate = new Date(sprint.start_date);
    const endDate = new Date(sprint.end_date);
    return now >= startDate && now <= endDate && sprint.status === 'open';
  }) || sprints.find(sprint => sprint.status === 'open');

  // Find the task's assigned sprint - use the sprint data included in the task object first
  const taskSprint = task?.sprint || task?.growth_sprints || (task?.sprint_id ? taskBrandSprints.find(sprint => sprint.id === task.sprint_id) : null);
  const fetchAssignedSquadMember = async (assignedToId: string, skipStateUpdate = false) => {
    try {
      const {
        data,
        error
      } = await supabase.from('growth_squad').select('*').eq('id', assignedToId).single();
      if (error) throw error;
      if (!skipStateUpdate) {
        setAssignedSquadMember(data);
      }
      return data;
    } catch (error) {
      console.error('Error fetching assigned squad member:', error);
      if (!skipStateUpdate) {
        setAssignedSquadMember(null);
      }
      return null;
    }
  };

  const fetchOwnerSquadMember = async (userId: string, brandId?: string) => {
    try {
      const query = supabase.from('growth_squad').select('*').eq('user_id', userId);
      if (brandId) {
        query.eq('brand_id', brandId);
      }
      const { data, error } = await query.single();
      if (error) throw error;
      setOwnerSquadMember(data);
    } catch (error) {
      console.error('Error fetching owner squad member:', error);
      setOwnerSquadMember(null);
    }
  };
  
  // Initialize and sync local images with task images
  React.useEffect(() => {
    setLocalImages(task?.images || []);
    setLocalAttachments(task?.attachments || []);
  }, [task?.images, task?.attachments]);

  // Fetch all brand tags once when task loads
  useEffect(() => {
    const fetchBrandTags = async () => {
      if (!task?.brand_id || taskType !== 'foundational') {
        setAllBrandTags([]);
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('foundational_task_tags')
          .select('id, name, color')
          .eq('brand_id', task.brand_id)
          .order('name');
        
        if (error) throw error;
        setAllBrandTags(data || []);
      } catch (error) {
        console.error('Error fetching tags:', error);
        setAllBrandTags([]);
      }
    };
    
    fetchBrandTags();
  }, [task?.brand_id, taskType]);

  // Compute selected and available tags from state (no DB calls)
  const selectedTags = allBrandTags.filter(tag => tagIds.includes(tag.id));
  const availableTags = allBrandTags.filter(tag => !tagIds.includes(tag.id));

  // Handle tag selection/deselection with optimistic update
  const handleTagToggle = async (tagId: string, isCurrentlySelected: boolean) => {
    const newTagIds = isCurrentlySelected 
      ? tagIds.filter(id => id !== tagId)
      : [...tagIds, tagId];
    
    const previousTagIds = tagIds;
    
    // Immediate optimistic update
    setTagIds(newTagIds);
    updateSelectedTask({ tag_ids: newTagIds as any });
    
    // Background database update
    try {
      const { error } = await supabase
        .from('foundational_tasks')
        .update({ tag_ids: newTagIds })
        .eq('id', task.id);
      
      if (error) {
        console.error('Error updating tags:', error);
        toast({
          title: "Error",
          description: "Failed to update tags",
          variant: "destructive",
        });
        setTagIds(previousTagIds);
        updateSelectedTask({ tag_ids: previousTagIds as any });
      }
    } catch (err) {
      console.error('Unexpected error updating tags:', err);
      toast({
        title: "Error",
        description: "Failed to update tags",
        variant: "destructive",
      });
      setTagIds(previousTagIds);
      updateSelectedTask({ tag_ids: previousTagIds as any });
    }
  };

  if (!task) return null;

  const currentStatus = taskType === 'foundational' || taskType === 'recurring' ? task.status : task.task_status;
  const taskTitle = task.title || task.name || 'Untitled Task';
  const getStatusBadgeProps = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          variant: 'outline' as const,
          className: 'bg-yellow-50 text-yellow-700 border-yellow-200 cursor-pointer hover:bg-yellow-100'
        };
      case 'in_progress':
        return {
          variant: 'default' as const,
          className: 'bg-blue-100 text-blue-700 border-blue-200 cursor-pointer hover:bg-blue-200'
        };
      case 'done':
        return {
          variant: 'default' as const,
          className: 'bg-green-100 text-green-700 border-green-200 cursor-pointer hover:bg-green-200'
        };
      default:
        return {
          variant: 'outline' as const,
          className: 'cursor-pointer'
        };
    }
  };
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'in_progress':
        return 'In Progress';
      case 'done':
        return 'Completed';
      default:
        return status;
    }
  };
  const getNextStatus = (currentStatus: string) => {
    switch (currentStatus) {
      case 'pending':
        return 'in_progress';
      case 'in_progress':
        return 'done';
      case 'done':
        return 'pending';
      default:
        return 'pending';
    }
  };
  const handleStatusClick = async () => {
    const nextStatus = getNextStatus(currentStatus);
    
    // Check if time capture is required before completing task
    if (nextStatus === 'done' && taskCreationSettings?.require_time_for_completion) {
      if (!timeSpent || timeSpent === 0) {
        toast({
          title: "Time Required",
          description: "You must log time spent on this task before marking it as completed.",
          variant: "destructive",
          className: "z-[9999]"
        });
        return;
      }
    }
    
    setIsUpdatingStatus(true);
    try {
      let error;
      if (taskType === 'recurring') {
        ({
          error
        } = await supabase.from('recurring_tasks').update({
          status: nextStatus
        }).eq('id', task.id));
      } else if (taskType === 'foundational') {
        ({
          error
        } = await supabase.from('foundational_tasks').update({
          status: nextStatus
        }).eq('id', task.id));
      } else {
        ({
          error
        } = await supabase.from('growth_tasks').update({
          task_status: nextStatus
        }).eq('id', task.id));
      }
      if (error) throw error;
      
      // Optimistically update React Query cache
      const queryKey = taskType === 'foundational' 
        ? ['all-tasks', 'foundational'] 
        : taskType === 'recurring'
        ? ['all-tasks', 'recurring']
        : ['all-tasks', 'growth'];

      queryClient.setQueriesData({ queryKey }, (oldData: any) => {
        if (!oldData) return oldData;
        return oldData.map((t: any) => 
          t.id === task.id 
            ? { ...t, [taskType === 'foundational' || taskType === 'recurring' ? 'status' : 'task_status']: nextStatus }
            : t
        );
      });
      
      // Update the local task state immediately for UI responsiveness
      if (taskType === 'foundational' || taskType === 'recurring') {
        updateSelectedTask({ status: nextStatus });
      } else {
        updateSelectedTask({ task_status: nextStatus });
      }
      
      toast({
        title: "Success",
        description: `Task status updated to ${getStatusLabel(nextStatus)}`
      });
      console.log('🔄 TaskPopup: calling onStatusUpdate and onRefetch after status update');
      onStatusUpdate?.(task.id, nextStatus);
      onRefetch?.(); // Trigger refetch to update UI state
    } catch (error) {
      console.error('Error updating task status:', error);
      toast({
        title: "Error",
        description: "Failed to update task status",
        variant: "destructive"
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };
  const handleCommentsUpdate = async () => {
    setIsUpdatingComments(true);
    try {
      let error;
      if (taskType === 'recurring') {
        ({
          error
        } = await supabase.from('recurring_tasks').update({
          comments
        }).eq('id', task.id));
      } else if (taskType === 'foundational') {
        ({
          error
        } = await supabase.from('foundational_tasks').update({
          comments
        }).eq('id', task.id));
      } else {
        ({
          error
        } = await supabase.from('growth_tasks').update({
          comments
        }).eq('id', task.id));
      }
      if (error) throw error;
      toast({
        title: "Success",
        description: "Comments updated successfully"
      });
      onCommentsUpdate?.(task.id, comments);
      onRefetch?.(); // Trigger refetch to update UI state
    } catch (error) {
      console.error('Error updating comments:', error);
      toast({
        title: "Error",
        description: "Failed to update comments",
        variant: "destructive"
      });
    } finally {
      setIsUpdatingComments(false);
    }
  };
  const handleRemarksUpdate = async () => {
    setIsUpdatingRemarks(true);
    try {
      let error;
      if (taskType === 'recurring') {
        ({
          error
        } = await supabase.from('recurring_tasks').update({
          remarks
        }).eq('id', task.id));
      } else if (taskType === 'foundational') {
        ({
          error
        } = await supabase.from('foundational_tasks').update({
          remarks
        }).eq('id', task.id));
      } else {
        ({
          error
        } = await supabase.from('growth_tasks').update({
          remarks
        }).eq('id', task.id));
      }
      if (error) throw error;
      toast({
        title: "Success",
        description: "Generic comment updated successfully"
      });
    } catch (error) {
      console.error('Error updating remarks:', error);
      toast({
        title: "Error",
        description: "Failed to update generic comment",
        variant: "destructive"
      });
    } finally {
      setIsUpdatingRemarks(false);
    }
  };
  const handleTimeSpentUpdate = async () => {
    setIsUpdatingTimeSpent(true);
    try {
      // Step 1: Read current value FROM DATABASE (not from local state)
      let currentDbValue = 0;
      let brandId: string | undefined;

      if (taskType === 'recurring') {
        const { data, error } = await supabase
          .from('recurring_tasks')
          .select('time_spent, brand_id')
          .eq('id', task.id)
          .maybeSingle();
        if (error) throw error;
        currentDbValue = data?.time_spent || 0;
        brandId = data?.brand_id;
      } else if (taskType === 'foundational') {
        const { data, error } = await supabase
          .from('foundational_tasks')
          .select('time_spent, brand_id')
          .eq('id', task.id)
          .maybeSingle();
        if (error) throw error;
        currentDbValue = data?.time_spent || 0;
        brandId = data?.brand_id;
      } else {
        const { data, error } = await supabase
          .from('growth_tasks')
          .select('time_spent, brand_id')
          .eq('id', task.id)
          .maybeSingle();
        if (error) throw error;
        currentDbValue = data?.time_spent || 0;
        brandId = data?.brand_id;
      }

      // Step 2: Calculate the difference (user's intended change from DB baseline)
      const minutesAdded = timeSpent - previousTimeSpent;
      const newTotal = Math.max(0, currentDbValue + minutesAdded);

      // Step 3: Write to DB with .select() verification
      let confirmedValue = newTotal;

      if (taskType === 'recurring') {
        const { data, error } = await supabase
          .from('recurring_tasks')
          .update({ time_spent: newTotal })
          .eq('id', task.id)
          .select('time_spent')
          .single();
        if (error) throw error;
        confirmedValue = data?.time_spent ?? newTotal;
      } else if (taskType === 'foundational') {
        const { data, error } = await supabase
          .from('foundational_tasks')
          .update({ time_spent: newTotal })
          .eq('id', task.id)
          .select('time_spent')
          .single();
        if (error) throw error;
        confirmedValue = data?.time_spent ?? newTotal;
      } else {
        const { data, error } = await supabase
          .from('growth_tasks')
          .update({ time_spent: newTotal })
          .eq('id', task.id)
          .select('time_spent')
          .single();
        if (error) throw error;
        confirmedValue = data?.time_spent ?? newTotal;
      }

      // Step 4: Update local state with CONFIRMED DB value
      setTimeSpent(confirmedValue);
      
      // Step 5: Log time entry to universal time tracking with correct source
      if (minutesAdded !== 0 && (brandId || task?.brand_id)) {
        await logTimeEntry({
          brandId: brandId || task.brand_id,
          taskId: task.id,
          taskType: taskType as 'foundational' | 'growth' | 'recurring',
          entryType: 'time_spent',
          minutesAdded: minutesAdded,
          previousTotal: currentDbValue,
          newTotal: confirmedValue,
          source: timeSource
        });
      }

      // Update tracking state with confirmed value
      setPreviousTimeSpent(confirmedValue);
      setTimeSource('manual'); // Reset source after logging
      
      // Update the task object with the confirmed time_spent value
      if (task) {
        (task as any).time_spent = confirmedValue;
      }
      
      toast({
        title: "Success",
        description: "Time spent updated successfully"
      });
    } catch (error) {
      console.error('Error updating time spent:', error);
      toast({
        title: "Error",
        description: "Failed to update time spent. Please try again.",
        variant: "destructive"
      });
      // Refetch the actual DB value to resync local state
      try {
        let dbValue = 0;
        if (taskType === 'recurring') {
          const { data } = await supabase.from('recurring_tasks').select('time_spent').eq('id', task.id).maybeSingle();
          dbValue = data?.time_spent || 0;
        } else if (taskType === 'foundational') {
          const { data } = await supabase.from('foundational_tasks').select('time_spent').eq('id', task.id).maybeSingle();
          dbValue = data?.time_spent || 0;
        } else {
          const { data } = await supabase.from('growth_tasks').select('time_spent').eq('id', task.id).maybeSingle();
          dbValue = data?.time_spent || 0;
        }
        setTimeSpent(dbValue);
        setPreviousTimeSpent(dbValue);
      } catch (refetchError) {
        console.error('Error refetching time spent:', refetchError);
      }
    } finally {
      setIsUpdatingTimeSpent(false);
    }
  };
  const getTaskTypeIcon = () => {
    return taskType === 'foundational' || taskType === 'recurring' ? <FileText className="h-4 w-4" /> : <Zap className="h-4 w-4" />;
  };
  const handleAssignmentChange = async (squadMemberId: string | null) => {
    if (!task) return;
    setIsUpdatingAssignment(true);
    try {
      let error;
      if (taskType === 'recurring') {
        ({
          error
        } = await supabase.from('recurring_tasks').update({
          assigned_to: squadMemberId
        }).eq('id', task.id));
      } else if (taskType === 'foundational') {
        ({
          error
        } = await supabase.from('foundational_tasks').update({
          assigned_to: squadMemberId
        }).eq('id', task.id));
      } else {
        ({
          error
        } = await supabase.from('growth_tasks').update({
          assigned_to: squadMemberId
        }).eq('id', task.id));
      }
      if (error) throw error;
      const nextAssignee = squadMemberId || 'unassigned';
      setCurrentAssigneeId(nextAssignee as string | 'unassigned');
      if (squadMemberId) {
        await fetchAssignedSquadMember(squadMemberId);
      } else {
        setAssignedSquadMember(null);
      }
      toast({
        title: "Success",
        description: squadMemberId ? "Task assigned successfully" : "Task assignment removed"
      });
    } catch (error) {
      console.error('Error updating task assignment:', error);
      toast({
        title: "Error",
        description: "Failed to update task assignment",
        variant: "destructive"
      });
    } finally {
      setIsUpdatingAssignment(false);
    }
  };

  const handleProjectChange = async (projectId: string | null) => {
    if (!task) return;
    setIsUpdatingProject(true);
    try {
      let error;
      if (taskType === 'recurring') {
        ({ error } = await supabase.from('recurring_tasks').update({
          project_id: projectId
        }).eq('id', task.id));
      } else if (taskType === 'foundational') {
        ({ error } = await supabase.from('foundational_tasks').update({
          project_id: projectId
        }).eq('id', task.id));
      } else {
        ({ error } = await supabase.from('growth_tasks').update({
          project_id: projectId
        }).eq('id', task.id));
      }
      if (error) throw error;
      setCurrentProjectId(projectId);
      toast({
        title: "Success",
        description: projectId ? "Project updated successfully" : "Project removed"
      });
    } catch (error) {
      console.error('Error updating task project:', error);
      toast({
        title: "Error",
        description: "Failed to update task project",
        variant: "destructive"
      });
    } finally {
      setIsUpdatingProject(false);
    }
  };

  const handleSprintChange = async (sprintId: string | null) => {
    if (!task) return;
    setIsUpdatingSprint(true);
    try {
      let error;
      if (taskType === 'recurring') {
        ({ error } = await supabase.from('recurring_tasks').update({
          sprint_id: sprintId
        }).eq('id', task.id));
      } else if (taskType === 'foundational') {
        ({ error } = await supabase.from('foundational_tasks').update({
          sprint_id: sprintId
        }).eq('id', task.id));
      } else {
        ({ error } = await supabase.from('growth_tasks').update({
          sprint_id: sprintId
        }).eq('id', task.id));
      }
      if (error) throw error;

      // Update task object
      if (task) {
        task.sprint_id = sprintId;
      }

      toast({
        title: "Success",
        description: sprintId ? "Task assigned to sprint successfully" : "Sprint assignment removed"
      });
      
      onRefetch?.(); // Trigger refetch to update UI state
    } catch (error) {
      console.error('Error updating sprint assignment:', error);
      toast({
        title: "Error",
        description: "Failed to update sprint assignment",
        variant: "destructive"
      });
    } finally {
      setIsUpdatingSprint(false);
    }
  };

  const handleWatchersUpdate = async (newWatchers: string[]) => {
    if (!task) return;
    setIsUpdatingWatchers(true);
    try {
      let error;
      if (taskType === 'recurring') {
        ({ error } = await supabase
          .from('recurring_tasks')
          .update({ watchers: newWatchers })
          .eq('id', task.id));
      } else if (taskType === 'foundational') {
        ({ error } = await supabase
          .from('foundational_tasks')
          .update({ watchers: newWatchers })
          .eq('id', task.id));
      } else {
        ({ error } = await supabase
          .from('growth_tasks')
          .update({ watchers: newWatchers })
          .eq('id', task.id));
      }
      
      if (error) throw error;
      
      // Update local state
      setWatchers(newWatchers);
      if (task) {
        task.watchers = newWatchers;
      }
      
      toast({
        title: "Success",
        description: "Watchers updated successfully"
      });
      
      // Trigger parent refetch
      onRefetch?.();
    } catch (error) {
      console.error('Error updating watchers:', error);
      toast({
        title: "Error",
        description: "Failed to update watchers",
        variant: "destructive"
      });
    } finally {
      setIsUpdatingWatchers(false);
    }
  };
  
  const handlePhaseChange = async (newPhaseId: string) => {
    if (!task) return;
    setIsUpdatingPhase(true);
    try {
      // Only growth_tasks have phase_id
      const { error } = await supabase
        .from('growth_tasks')
        .update({ phase_id: newPhaseId || null })
        .eq('id', task.id);
      
      if (error) throw error;
      
      setPhaseId(newPhaseId || undefined);
      
      toast({
        title: "Success",
        description: "Phase updated successfully"
      });
      
      onRefetch?.();
    } catch (error) {
      console.error('Error updating phase:', error);
      toast({
        title: "Error",
        description: "Failed to update phase",
        variant: "destructive"
      });
    } finally {
      setIsUpdatingPhase(false);
    }
  };
  
  const handleDueDateUpdate = async (newDate: Date | undefined) => {
    console.log('handleDueDateUpdate called with:', newDate);
    console.log('Task ID:', task.id, 'Task Type:', taskType);
    setIsUpdatingDueDate(true);
    try {
      let error;
      const dueDateValue = newDate ? newDate.toISOString().split('T')[0] : null;
      console.log('Formatted due date value:', dueDateValue);
      let assignedSprint = null;

      // Smart sprint assignment: if no sprint is assigned and a date is selected
      if (newDate && !task.sprint_id) {
        // Find sprint that contains the selected date
        const targetSprint = taskBrandSprints.find(sprint => {
          const sprintStart = new Date(sprint.start_date);
          const sprintEnd = new Date(sprint.end_date);
          return newDate >= sprintStart && newDate <= sprintEnd && sprint.status === 'open';
        });
        if (targetSprint) {
          assignedSprint = targetSprint;
        }
      }

      // Update the task with due date and potentially sprint assignment
      const updateData = assignedSprint ? {
        due_date: dueDateValue,
        sprint_id: assignedSprint.id
      } : {
        due_date: dueDateValue
      };
      let data;
      if (taskType === 'recurring') {
        const result = await supabase.from('recurring_tasks').update(updateData).eq('id', task.id).select('id, due_date');
        error = result.error;
        data = result.data;
      } else if (taskType === 'foundational') {
        const result = await supabase.from('foundational_tasks').update(updateData).eq('id', task.id).select('id, due_date');
        error = result.error;
        data = result.data;
      } else {
        const result = await supabase.from('growth_tasks').update(updateData).eq('id', task.id).select('id, due_date');
        error = result.error;
        data = result.data;
      }
      console.log('Database update result - error:', error, 'data:', data);
      if (error) throw error;
      
      // Verify update actually succeeded
      if (!data || data.length === 0) {
        throw new Error('Update failed - no rows were updated. You may not have permission or the task data is invalid.');
      }
      console.log('Update successful, returned data:', data);
      setDueDate(newDate);
      
      // Update context so changes persist
      updateSelectedTask({ due_date: dueDateValue });

      // Sync due date to Google Calendar event if task was synced and brand has sync on
      const calendarEventId = taskType === 'foundational' ? (task as { google_calendar_event_id?: string }).google_calendar_event_id : null;
      if (
        calendarEventId &&
        taskCreationSettings?.sync_with_google_calendar_on_tasks &&
        newDate &&
        updateCalendarEvent
      ) {
        const eventDate = new Date(newDate);
        eventDate.setHours(9, 0, 0, 0);
        const start = new Date(eventDate);
        const durationMinutes = (task as { time_estimated?: number }).time_estimated && (task as { time_estimated?: number }).time_estimated! > 0
          ? (task as { time_estimated?: number }).time_estimated!
          : 60;
        const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
        await updateCalendarEvent(calendarEventId, {
          start: start.toISOString(),
          end: end.toISOString(),
        });
      }

      // Update local task data if sprint was assigned
      if (assignedSprint && task) {
        task.sprint_id = assignedSprint.id;
        task.sprint = {
          id: assignedSprint.id,
          name: assignedSprint.name
        };
      }

      // Show appropriate success messages
      if (newDate) {
        toast({
          title: "Success",
          description: "Due date updated successfully"
        });
        if (assignedSprint) {
          toast({
            title: "Sprint Assignment",
            description: `Task assigned to sprint ${assignedSprint.name}`
          });
        }
      } else {
        toast({
          title: "Success",
          description: "Due date removed"
        });
      }
      
      // Invalidate queries and trigger refetch
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['foundational-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['growth-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['recurring-tasks'] });
      if (onRefetch) onRefetch();
    } catch (error) {
      console.error('Error updating due date:', error);
      toast({
        title: "Error",
        description: "Failed to update due date",
        variant: "destructive"
      });
    } finally {
      setIsUpdatingDueDate(false);
    }
  };
  const handleCopyLink = async () => {
    if (!task.brand_id) {
      toast({
        title: "Error",
        description: "Unable to copy link - brand information missing",
        variant: "destructive"
      });
      return;
    }

    console.log('TaskPopup Copy Debug:', {
      taskId: task.id,
      brandId: task.brand_id,
      brandSlug: task.brands?.slug,
      taskSlug: task.slug,
      hasBothSlugs: !!(task.brands?.slug && task.slug)
    });

    // Try to use friendly URL if brand slug is available
    let taskUrl;
    if (task.brands?.slug) {
      // Generate task slug from title if not present
      const taskSlug = task.slug || generateSlug(task.title);
      taskUrl = `${window.location.origin}${generateTaskUrl(task.brands.slug, taskSlug)}`;
    } else {
      // Fallback to technical URL
      taskUrl = `${window.location.origin}${generateTechnicalTaskUrl(task.brand_id, task.id)}`;
    }
    try {
      await navigator.clipboard.writeText(taskUrl);
      toast({
        title: "Success",
        description: task.brands?.slug ? "Friendly task link copied to clipboard" : "Task link copied to clipboard"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy link to clipboard",
        variant: "destructive"
      });
    }
  };

  const handleDescriptionUpdate = async () => {
    if (!task) return;
    
    try {
      let error;
      if (taskType === 'recurring') {
        ({ error } = await supabase
          .from('recurring_tasks')
          .update({ description: editedDescription })
          .eq('id', task.id));
      } else if (taskType === 'foundational') {
        ({ error } = await supabase
          .from('foundational_tasks')
          .update({ description: editedDescription })
          .eq('id', task.id));
      } else {
        ({ error } = await supabase
          .from('growth_tasks')
          .update({ description: editedDescription })
          .eq('id', task.id));
      }
      
      if (error) throw error;
      
      // Update local task data
      task.description = editedDescription;
      setIsEditingDescription(false);
      
      toast({
        title: "Success",
        description: "Description updated successfully"
      });
      onRefetch?.();
    } catch (error) {
      console.error('Error updating description:', error);
      toast({
        title: "Error",
        description: "Failed to update description",
        variant: "destructive"
      });
    }
  };

  const handleTitleUpdate = async () => {
    if (!task) return;
    
    try {
      let error;
      
      if (taskType === 'recurring') {
        ({ error } = await supabase
          .from('recurring_tasks')
          .update({ title: editedTitle })
          .eq('id', task.id));
      } else if (taskType === 'foundational') {
        ({ error } = await supabase
          .from('foundational_tasks')
          .update({ title: editedTitle })
          .eq('id', task.id));
      } else {
        ({ error } = await supabase
          .from('growth_tasks')
          .update({ name: editedTitle })
          .eq('id', task.id));
      }
      
      if (error) throw error;
      
      // Update local task data
      if (taskType === 'growth') {
        task.name = editedTitle;
      } else {
        task.title = editedTitle;
      }
      setIsEditingTitle(false);
      
      toast({
        title: "Success",
        description: "Title updated successfully"
      });
      onRefetch?.();
    } catch (error) {
      console.error('Error updating title:', error);
      toast({
        title: "Error",
        description: "Failed to update title",
        variant: "destructive"
      });
    }
  };

  const handleImageUpload = async (files: FileList) => {
    console.log('🔥🔥🔥 handleImageUpload called with files:', files);
    if (!task || !files.length) {
      console.log('❌ No task or no files, returning');
      return;
    }
    
    console.log('🔥 Starting image upload process');
    console.log('🔥 Task:', task);
    console.log('🔥 Task Type:', taskType);
    setIsUploadingImage(true);
    
    try {
      const uploadedImages = [];
      
      for (const file of Array.from(files)) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast({
            title: "Error",
            description: `${file.name} is not a valid image file`,
            variant: "destructive"
          });
          continue;
        }
        
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          toast({
            title: "Error",
            description: `${file.name} is too large. Maximum size is 5MB`,
            variant: "destructive"
          });
          continue;
        }
        
        // Generate unique filename
        const fileExt = file.name.split('.').pop();
        const fileName = `${task.id}-${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `task-images/${fileName}`;
        
        // Upload to Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('task-images')
          .upload(filePath, file);
          
        if (uploadError) throw uploadError;
        
        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('task-images')
          .getPublicUrl(filePath);
          
        uploadedImages.push({
          url: publicUrl,
          name: file.name,
          path: filePath
        });
      }
      
      if (uploadedImages.length === 0) {
        console.log('❌ No images were uploaded successfully');
        return;
      }
      
      console.log('🔥 About to update database with images');
      // Update task with new images
      const currentImages = task.images || [];
      const updatedImages = [...currentImages, ...uploadedImages];
      console.log('🔥 Current images:', currentImages);
      console.log('🔥 Uploaded images:', uploadedImages);
      console.log('🔥 Updated images array:', updatedImages);
      console.log('🔥 Task type:', taskType);
      console.log('🔥 Task ID:', task.id);
      
      let error;
      if (taskType === 'recurring') {
        console.log('🔥 Updating recurring_tasks table');
        ({ error } = await supabase
          .from('recurring_tasks')
          .update({ images: updatedImages })
          .eq('id', task.id));
      } else if (taskType === 'foundational') {
        console.log('🔥 Updating foundational_tasks table');
        ({ error } = await supabase
          .from('foundational_tasks')
          .update({ images: updatedImages })
          .eq('id', task.id));
      } else {
        console.log('🔥 Updating growth_tasks table');
        ({ error } = await supabase
          .from('growth_tasks')
          .update({ images: updatedImages })
          .eq('id', task.id));
      }
      
      if (error) {
        console.error('❌ Database update error:', error);
        throw error;
      }
      
      console.log('✅ Database updated successfully');
      
      // Update local task data and local state
      task.images = updatedImages;
      setLocalImages(updatedImages);
      
      console.log('✅ Local state updated');
      
      toast({
        title: "Success",
        description: `${uploadedImages.length} image(s) uploaded successfully`
      });
      
      console.log('✅ Calling onRefetch');
      onRefetch?.();
    } catch (error) {
      console.error('Error uploading images:', error);
      toast({
        title: "Error",
        description: "Failed to upload images",
        variant: "destructive"
      });
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleAttachmentUpload = async (files: FileList) => {
    console.log('📎 handleAttachmentUpload called with files:', files);
    if (!task || !files.length) {
      console.log('❌ No task or no files, returning');
      return;
    }
    
    console.log('📎 Starting attachment upload process');
    setIsUploadingAttachment(true);
    
    try {
      const uploadedAttachments = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log('📎 Processing file:', file.name, 'Type:', file.type);
        
        // Generate unique filename
        const timestamp = Date.now();
        const randomString = Math.random().toString(36).substring(2, 15);
        const fileExtension = file.name.split('.').pop();
        const fileName = `task-attachments/${task.id}-${timestamp}-${randomString}.${fileExtension}`;
        
        console.log('📎 Uploading to path:', fileName);
        
        // Upload to Supabase storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('task-attachments')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) {
          console.error('📎 Upload error:', uploadError);
          throw uploadError;
        }
        
        console.log('📎 Upload successful:', uploadData);
        
        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('task-attachments')
          .getPublicUrl(fileName);
        
        console.log('📎 Public URL:', publicUrl);
        
        uploadedAttachments.push({
          url: publicUrl,
          name: file.name,
          path: fileName,
          type: file.type,
          size: file.size
        });
      }
      
      // Update task attachments
      const updatedAttachments = [...localAttachments, ...uploadedAttachments];
      console.log('📎 Updated attachments:', updatedAttachments);
      
      // Update task in database
      let error;
      if (taskType === 'recurring') {
        ({ error } = await supabase
          .from('recurring_tasks')
          .update({ attachments: updatedAttachments })
          .eq('id', task.id));
      } else if (taskType === 'foundational') {
        ({ error } = await supabase
          .from('foundational_tasks')
          .update({ attachments: updatedAttachments })
          .eq('id', task.id));
      } else {
        ({ error } = await supabase
          .from('growth_tasks')
          .update({ attachments: updatedAttachments })
          .eq('id', task.id));
      }
      
      if (error) {
        console.error('❌ Database update error:', error);
        throw error;
      }
      
      console.log('✅ Database updated successfully');
      
      // Update local task data and local state
      task.attachments = updatedAttachments;
      setLocalAttachments(updatedAttachments);
      
      console.log('✅ Local state updated');
      
      toast({
        title: "Success",
        description: `${uploadedAttachments.length} attachment(s) uploaded successfully`
      });
      
      console.log('✅ Calling onRefetch');
      console.log('🔄 About to call onRefetch to refresh parent data');
      onRefetch?.();
      console.log('🔄 onRefetch called successfully');
    } catch (error: any) {
      console.error('Error uploading attachments:', error);
      
      let errorMessage = "Failed to upload attachments";
      
      // Check for specific error types
      if (error.message?.includes('exceeded the maximum allowed size') || error.statusCode === '413') {
        errorMessage = "File size too large. Please choose a smaller file (max 50MB).";
      } else if (error.message?.includes('not allowed')) {
        errorMessage = "File type not allowed. Please choose a PDF, DOC, TXT, or XLS file.";
      }
      
      toast({
        title: "Upload Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const handleImageDelete = async (imageIndex: number) => {
    if (!localImages || imageIndex < 0 || imageIndex >= localImages.length) return;
    
    try {
      const imageToDelete = localImages[imageIndex];
      
      // If image has a path, delete from storage
      if (imageToDelete.path) {
        const { error: storageError } = await supabase.storage
          .from('task-images')
          .remove([imageToDelete.path]);
          
        if (storageError) {
          console.error('Error deleting from storage:', storageError);
        }
      }
      
      // Remove from array
      const updatedImages = localImages.filter((_, index) => index !== imageIndex);
      
      // Update task in database
      let error;
      if (taskType === 'recurring') {
        ({ error } = await supabase
          .from('recurring_tasks')
          .update({ images: updatedImages })
          .eq('id', task.id));
      } else if (taskType === 'foundational') {
        ({ error } = await supabase
          .from('foundational_tasks')
          .update({ images: updatedImages })
          .eq('id', task.id));
      } else {
        ({ error } = await supabase
          .from('growth_tasks')
          .update({ images: updatedImages })
          .eq('id', task.id));
      }
      
      if (error) {
        throw error;
      }
      
      console.log('Database updated successfully');
      
      // Update local task data and local state
      task.images = updatedImages;
      setLocalImages(updatedImages);
      
      toast({
        title: "Success",
        description: "Image deleted successfully"
      });
      
      onRefetch?.();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        title: "Error",
        description: "Failed to delete image",
        variant: "destructive"
      });
    }
  };

  const triggerImageUpload = () => {
    console.log('🔥 triggerImageUpload called');
    if (fileInputRef.current) {
      console.log('🔥 File input ref exists, clicking...');
      fileInputRef.current.click();
      console.log('🔥 File input clicked via ref');
    } else {
      console.log('❌ File input ref is null');
    }
  };

  const triggerAttachmentUpload = () => {
    console.log('📎 triggerAttachmentUpload called');
    if (attachmentInputRef.current) {
      console.log('📎 Attachment input ref exists, clicking...');
      attachmentInputRef.current.click();
      console.log('📎 Attachment input clicked via ref');
    } else {
      console.log('❌ Attachment input ref is null');
    }
  };

  const handleAttachmentDelete = async (attachmentIndex: number) => {
    if (!localAttachments || attachmentIndex < 0 || attachmentIndex >= localAttachments.length) return;
    
    try {
      const attachmentToDelete = localAttachments[attachmentIndex];
      
      // If attachment has a path, delete from storage
      if (attachmentToDelete.path) {
        const { error: storageError } = await supabase.storage
          .from('task-images')
          .remove([attachmentToDelete.path]);
          
        if (storageError) {
          console.error('Storage deletion error:', storageError);
        }
      }
      
      // Remove from local array
      const updatedAttachments = localAttachments.filter((_, index) => index !== attachmentIndex);
      
      // Update database
      let error;
      if (taskType === 'recurring') {
        ({ error } = await supabase
          .from('recurring_tasks')
          .update({ attachments: updatedAttachments })
          .eq('id', task.id));
      } else if (taskType === 'foundational') {
        ({ error } = await supabase
          .from('foundational_tasks')
          .update({ attachments: updatedAttachments })
          .eq('id', task.id));
      } else {
        ({ error } = await supabase
          .from('growth_tasks')
          .update({ attachments: updatedAttachments })
          .eq('id', task.id));
      }
      
      if (error) throw error;
      
      // Update local state and task object
      setLocalAttachments(updatedAttachments);
      if (task) {
        task.attachments = updatedAttachments;
      }
      
      toast({
        title: "Success",
        description: "Attachment deleted successfully"
      });
      
      // Trigger refetch to update parent data
      onRefetch?.();
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast({
        title: "Error",
        description: "Failed to delete attachment",
        variant: "destructive"
      });
    }
  };

  const handleViewDocHubItem = async (type: 'tip' | 'file' | 'mom', itemId: string) => {
    try {
      let title = '';
      let content = '';
      
      if (type === 'tip') {
        const { data, error } = await supabase
          .from('os_doc_hub_tips')
          .select('name, content')
          .eq('id', itemId)
          .single();
        if (error) throw error;
        title = data?.name || 'Tip';
        content = data?.content || '';
      } else if (type === 'file') {
        const { data, error } = await supabase
          .from('os_doc_hub_files')
          .select('name, link, images')
          .eq('id', itemId)
          .single();
        if (error) throw error;
        title = data?.name || 'File';
        // Check link first, then images array for file URL
        const fileUrl = data?.link || (Array.isArray(data?.images) && data.images.length > 0 ? String(data.images[0]) : null);
        if (fileUrl) {
          window.open(fileUrl, '_blank');
          return;
        }
        content = 'No file link available';
      } else if (type === 'mom') {
        // Try operations moms first
        const { data: opMom, error: opError } = await supabase
          .from('operations_moms')
          .select('filename, transcript_content, summary')
          .eq('id', itemId)
          .single();
        if (!opError && opMom) {
          title = opMom?.filename || 'MOM';
          content = opMom?.transcript_content || opMom?.summary || '';
        } else {
          // Try sales moms
          const { data: salesMom, error: salesError } = await supabase
            .from('os_sales_moms')
            .select('client_name, transcript_content, description')
            .eq('id', itemId)
            .single();
          if (salesError) throw salesError;
          title = salesMom?.client_name || 'MOM';
          content = salesMom?.transcript_content || salesMom?.description || '';
        }
      }
      
      setDocHubViewContent({ title, content, type });
    } catch (error) {
      console.error('Error fetching DocHub item:', error);
      toast({
        title: "Error",
        description: "Failed to load content",
        variant: "destructive"
      });
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('🔥🔥🔥 FILE INPUT CHANGE TRIGGERED!', e);
    const files = e.target.files;
    console.log('🔥 Selected files:', files);
    console.log('🔥 Files length:', files?.length);
    console.log('🔥 First file:', files?.[0]);
    
    if (files && files.length > 0) {
      console.log('🔥 About to call handleImageUpload with files');
      handleImageUpload(files);
      console.log('🔥 Called handleImageUpload');
    } else {
      console.log('❌ No files selected');
    }
    
    // Reset the input value so the same file can be selected again
    e.target.value = '';
    console.log('🔥 Reset input value');
  };

  const handleAttachmentInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('📎📎📎 ATTACHMENT INPUT CHANGE TRIGGERED!', e);
    const files = e.target.files;
    console.log('📎 Selected files:', files);
    console.log('📎 Files length:', files?.length);
    console.log('📎 First file:', files?.[0]);
    
    if (files && files.length > 0) {
      console.log('📎 About to call handleAttachmentUpload with files');
      handleAttachmentUpload(files);
      console.log('📎 Called handleAttachmentUpload');
    } else {
      console.log('❌ No attachment files selected');
    }
    
    // Reset the input value so the same file can be selected again
    e.target.value = '';
    console.log('📎 Reset attachment input value');
  };
  
  return <Dialog open={isOpen} onOpenChange={onClose}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
      {/* Hidden attachment input */}
      <input
        ref={attachmentInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.csv"
        multiple
        style={{ display: 'none' }}
        onChange={handleAttachmentInputChange}
      />
      <DialogContent className={cn("max-w-full sm:max-w-[800px] w-full max-h-[90vh] overflow-y-auto p-0", task.internal && "bg-yellow-50")}>
        {/* Grey Header */}
        <div className={cn("w-full h-12 bg-muted border-b flex items-center px-6", task.internal && "bg-yellow-100")}>
          {task.internal && (
            <div className="flex items-center gap-2 mr-4">
              <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
              <span className="text-xs font-medium text-yellow-700">INTERNAL</span>
            </div>
          )}
          {isEditingTitle ? (
            <div className="flex items-center gap-2 w-full">
              <input
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                className="text-sm font-medium bg-transparent border-none outline-none flex-1"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleTitleUpdate();
                  } else if (e.key === 'Escape') {
                    setIsEditingTitle(false);
                    setEditedTitle(taskTitle);
                  }
                }}
              />
              <Button onClick={handleTitleUpdate} size="sm" variant="ghost" className="h-6 px-2 text-xs">
                Save
              </Button>
              <Button 
                onClick={() => {
                  setIsEditingTitle(false);
                  setEditedTitle(taskTitle);
                }} 
                size="sm" 
                variant="ghost" 
                className="h-6 px-2 text-xs"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <h2 className="text-sm font-medium text-foreground break-words min-w-0">{taskTitle}</h2>
          )}
        </div>
        
        <div className="px-4 sm:px-6 py-6 relative">
        {/* Status on left, Tags and Icons on right - same row */}
        <div className="absolute -top-2 left-4 sm:left-6 right-4 sm:right-2 flex flex-wrap sm:flex-nowrap items-center justify-between gap-2">
          <Badge {...getStatusBadgeProps(currentStatus)} onClick={handleStatusClick} className={`${getStatusBadgeProps(currentStatus).className} transition-colors px-3 py-1 text-sm flex-shrink-0`}>
            {isUpdatingStatus ? 'Updating...' : getStatusLabel(currentStatus)}
          </Badge>
          
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap flex-shrink-0">
            {/* Selected Tags - Only for foundational tasks */}
            {taskType === 'foundational' && selectedTags.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap">
                {selectedTags.map(tag => (
                  <Badge
                    key={tag.id}
                    onClick={() => handleTagToggle(tag.id, true)}
                    className="cursor-pointer text-xs px-2 py-0.5 hover:opacity-80 transition-opacity"
                    style={{
                      backgroundColor: tag.color,
                      color: '#fff',
                      border: 'none'
                    }}
                  >
                    {tag.name}
                  </Badge>
                ))}
              </div>
            )}
            
            {/* Available/Unselected Tags - Only for foundational tasks */}
            {taskType === 'foundational' && availableTags.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap">
                {availableTags.map(tag => (
                  <Badge
                    key={tag.id}
                    onClick={() => handleTagToggle(tag.id, false)}
                    variant="outline"
                    className="cursor-pointer text-xs px-2 py-0.5 hover:bg-muted transition-colors"
                    style={{
                      borderColor: tag.color,
                      color: tag.color,
                    }}
                  >
                    {tag.name}
                  </Badge>
                ))}
              </div>
            )}
            
            {/* Metadata Icons */}
            <div className="flex items-center gap-0.5 sm:gap-1">
            {/* Priority Icon */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Flag className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${
                    task.priority === 'High' ? 'text-red-500' : 
                    task.priority === 'Medium' ? 'text-yellow-500' : 
                    task.priority === 'Low' ? 'text-green-500' : 'text-gray-400'
                  }`} />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Priority: {task.priority}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* Difficulty Icon */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Target className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ml-1 sm:ml-2 ${
                    task.difficulty === 'Hard' ? 'text-red-500' : 
                    task.difficulty === 'Medium' ? 'text-yellow-500' : 
                    task.difficulty === 'Easy' ? 'text-green-500' : 'text-gray-400'
                  }`} />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Difficulty: {task.difficulty}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* Vertical Separator */}
            <div className="h-6 w-px bg-border mx-0.5 sm:mx-1"></div>
            
            <Button variant="ghost" size="sm" onClick={handleCopyLink} className="h-7 w-7 sm:h-8 sm:w-8 p-0" title="Copy task link">
              <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                console.log('🔥🔥🔥 DIRECT UPLOAD BUTTON CLICKED!!!');
                triggerImageUpload();
              }} 
              className="h-7 w-7 sm:h-8 sm:w-8 p-0" 
              title="Add New Image"
            >
              <ImagePlus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                console.log('📎📎📎 DIRECT ATTACHMENT UPLOAD BUTTON CLICKED!!!');
                triggerAttachmentUpload();
              }} 
              className="h-7 w-7 sm:h-8 sm:w-8 p-0" 
              title="Add Attachment (PDF, DOC, TXT, XLS)"
              disabled={isUploadingAttachment}
            >
              <Paperclip className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => {
              if (task.images && task.images.length > 0) {
                setShowImageViewer(true);
              }
            }} className={cn("h-7 w-7 sm:h-8 sm:w-8 p-0", task.images && task.images.length > 0 ? "text-green-500 hover:text-green-600" : "text-muted-foreground cursor-not-allowed")} title={task.images && task.images.length > 0 ? "View task images" : "No images"} disabled={!task.images || task.images.length === 0}>
              <Image className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Button>
           <DropdownMenu>
             <DropdownMenuTrigger asChild>
               <Button 
                 variant="ghost" 
                 size="sm" 
                 className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                 onClick={() => console.log('🔥🔥🔥 DROPDOWN TRIGGER CLICKED!')}
               >
                <MoreVertical className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-[1200] bg-popover">
              <DropdownMenuItem
                onClick={() => setShowImageViewer(true)}
                disabled={!task.images || task.images.length === 0}
                className="flex items-center gap-2"
              >
                <Edit3 className="h-4 w-4" />
                Edit Images
              </DropdownMenuItem>
               <DropdownMenuItem
                onClick={() => {
                  console.log('🔥🔥🔥 ADD NEW IMAGE CLICKED!!!');
                  triggerImageUpload();
                }}
                disabled={isUploadingImage}
                className="flex items-center gap-2"
              >
                <ImagePlus className="h-4 w-4" />
                {isUploadingImage ? 'Uploading...' : 'Add New Image'}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  console.log('📎📎📎 ADD NEW ATTACHMENT CLICKED!!!');
                  triggerAttachmentUpload();
                }}
                disabled={isUploadingAttachment}
                className="flex items-center gap-2"
              >
                <Paperclip className="h-4 w-4" />
                {isUploadingAttachment ? 'Uploading...' : 'Add Attachment'}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setIsEditingDescription(true)}
                className="flex items-center gap-2"
              >
                <Edit3 className="h-4 w-4" />
                Edit Description
              </DropdownMenuItem>
               <DropdownMenuItem
                onClick={() => setIsEditingTitle(true)}
                className="flex items-center gap-2"
              >
                <Edit3 className="h-4 w-4" />
                Edit Title
              </DropdownMenuItem>
              {taskType === 'foundational' && (
                <DropdownMenuItem
                  onClick={() => setShowTagsDialog(true)}
                  className="flex items-center gap-2"
                >
                  <Tag className="h-4 w-4" />
                  Manage Tags
                </DropdownMenuItem>
              )}
             </DropdownMenuContent>
           </DropdownMenu>
            </div>
          </div>
        </div>
        
        {/* Light separator line - full width end-to-end */}
        <div className="h-px bg-border my-4 -mx-4 sm:-mx-6"></div>
        
          {/* Two column layout: Due Date and Planned for */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 mb-2 sm:mb-4">
            {/* Due Date */}
            <div className="space-y-2 sm:space-y-2">
              <div className="flex items-center gap-2 sm:flex-col sm:items-start sm:gap-0">
                <Label className="text-sm font-medium text-muted-foreground whitespace-nowrap">Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" className={cn("flex-1 sm:w-full justify-start text-left font-normal text-sm h-8 pl-0", !dueDate && "text-muted-foreground")} disabled={isUpdatingDueDate}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {isUpdatingDueDate ? 'Updating...' : dueDate ? (
                        <DueDateTasksHoverCard dueDate={task.due_date} brandId={task.brand_id}>
                          <span className="flex items-center gap-1 cursor-pointer">
                            {format(dueDate, "MMM dd, yyyy")}
                            <span className={`text-xs ${(() => {
                              const daysDiff = differenceInDays(dueDate, new Date());
                              if (daysDiff < 0) return 'text-red-500';
                              if (daysDiff <= 3) return 'text-yellow-500';
                              return 'text-green-500';
                            })()}`}>
                              ({(() => {
                                const daysDiff = differenceInDays(dueDate, new Date());
                                if (daysDiff < 0) return `${Math.abs(daysDiff)} days ago`;
                                return `in ${daysDiff} days`;
                              })()})
                            </span>
                          </span>
                        </DueDateTasksHoverCard>
                      ) : <span>No date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent mode="single" selected={dueDate} onSelect={newDate => handleDueDateUpdate(newDate)} disabled={date => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    
                    // Check same-day restriction
                    if (taskCreationSettings?.allow_same_day_tasks === false) {
                      const dateToCheck = new Date(date);
                      dateToCheck.setHours(0, 0, 0, 0);
                      if (dateToCheck.getTime() === today.getTime()) {
                        return true; // Disable today
                      }
                    }
                    
                    // If task has an assigned sprint and off-sprint dates are not allowed, restrict dates to that sprint
                    if (taskCreationSettings?.allow_off_sprint_dates === false && taskSprint) {
                      const sprintStart = new Date(taskSprint.start_date);
                      const sprintEnd = new Date(taskSprint.end_date);
                      return date < sprintStart || date > sprintEnd;
                    }
                    
                    // Default: only prevent past dates
                    return date < today;
                  }} initialFocus className={cn("p-3 pointer-events-auto")} />
                    {dueDate && <div className="p-3 border-t">
                        <Button variant="outline" size="sm" onClick={() => handleDueDateUpdate(undefined)} className="w-full text-xs" disabled={isUpdatingDueDate}>
                          Clear Due Date
                        </Button>
                      </div>}
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Planned for */}
            <div className="space-y-2 sm:space-y-2">
              <div className="flex items-center gap-2 sm:flex-col sm:items-start sm:gap-0">
                <Label className="text-sm font-medium text-muted-foreground whitespace-nowrap">Planned for:</Label>
                {taskBrandSprints.length === 0 ? (
                  <button
                    onClick={async () => {
                      if (task.brand_id && currentBrandId !== task.brand_id) {
                        await selectBrand(task.brand_id);
                      }
                      window.location.href = '/sprints';
                    }}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    No sprint assign. Create →
                  </button>
                ) : (
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-sm">
                      {taskSprint?.name || 'No sprint'}
                    </span>
                    <Select 
                      value={task.sprint_id || 'unassigned'}
                      onValueChange={value => handleSprintChange(value === 'unassigned' ? null : value)} 
                      disabled={isUpdatingSprint}
                    >
                      <SelectTrigger className="h-8 text-sm w-[140px]">
                        <SelectValue placeholder="Change">
                          {isUpdatingSprint ? 'Updating...' : 'Change'}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="min-w-[220px]">
                        <SelectItem value="unassigned">
                          <span>No sprint</span>
                        </SelectItem>
                        {taskBrandSprints.map(sprint => (
                          <SelectItem key={sprint.id} value={sprint.id}>
                            <span>{sprint.name}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Two column layout: Owner and Assigned To */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 pt-1 sm:pt-2">
            {/* Owner - Left side */}
            <div className="space-y-1 sm:space-y-1">
              <div className="flex items-center gap-2 sm:flex-col sm:items-start sm:gap-1">
                <Label className="text-sm font-medium text-muted-foreground whitespace-nowrap">Owner</Label>
                <div className="flex items-center gap-2">
                  {ownerSquadMember ? (
                    <UserHoverCard squadMemberId={ownerSquadMember.id} brandId={task.brand_id}>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center">
                          {ownerSquadMember.alias?.slice(0, 2).toUpperCase() || 'U'}
                        </div>
                        <span className="text-sm">{ownerSquadMember.name}</span>
                      </div>
                    </UserHoverCard>
                  ) : (
                    <span className="text-sm text-muted-foreground">No owner</span>
                  )}
                </div>
              </div>
            </div>

            {/* Assigned To - Right side - Searchable */}
            <div className="space-y-1 sm:space-y-1">
              <div className="flex items-center gap-2 sm:flex-col sm:items-start sm:gap-1">
                <Label className="text-sm font-medium text-muted-foreground whitespace-nowrap">Assigned to:</Label>
                <div className="flex items-center gap-2 flex-1">
                  {assignedSquadMember ? (
                    <UserHoverCard squadMemberId={assignedSquadMember.id} brandId={task.brand_id}>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                          {assignedSquadMember.alias?.slice(0, 2).toUpperCase() || 'U'}
                        </div>
                        <span className="text-sm">{assignedSquadMember.name}</span>
                      </div>
                    </UserHoverCard>
                  ) : (
                    <span className="text-sm text-muted-foreground">Unassigned</span>
                  )}
                  <AssignedToInlineSelector
                    members={(taskBrandSquadMembers.length ? taskBrandSquadMembers : squadMembers)}
                    value={currentAssigneeId}
                    onValueChange={(val) => handleAssignmentChange(val === 'unassigned' ? null : val)}
                    disabled={isUpdatingAssignment}
                    isUpdating={isUpdatingAssignment}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Project Selector - Searchable (only if projects enabled for brand) */}
          {taskCreationSettings?.project_enabled !== false && (
          <div className="space-y-1 pt-1 sm:pt-2">
            <div className="flex items-center gap-2 sm:flex-col sm:items-start sm:gap-1">
              <Label className="text-sm font-medium text-muted-foreground whitespace-nowrap">Project:</Label>
              <div className="flex items-center gap-2 flex-1">
                <span className="text-sm">
                  {currentProjectId
                    ? taskBrandProjects.find(p => p.id === currentProjectId)?.name || 'Unknown project'
                    : 'No project'}
                </span>
                <ProjectInlineSelector
                  projects={taskBrandProjects.filter(p => p.status === 'active')}
                  value={currentProjectId || 'none'}
                  onValueChange={(val) => handleProjectChange(val === 'none' ? null : val)}
                  disabled={isUpdatingProject}
                  isUpdating={isUpdatingProject}
                />
              </div>
            </div>
          </div>
          )}

          {task.source_type === '2y3x' && (
            <div className="space-y-1 pt-2">
              <Label className="text-sm font-medium text-muted-foreground">Phase</Label>
              <PhaseSelector
                value={phaseId || ''}
                onValueChange={handlePhaseChange}
                disabled={isUpdatingPhase}
                brandId={task.brand_id}
                methodology="2y3x"
              />
            </div>
          )}

          {/* Description - Below owner with spacing */}
          {(task.description || isEditingDescription) && <div className="space-y-1 pt-4">
              <Label className="text-sm font-medium">Description</Label>
              {isEditingDescription ? (
                <div className="flex gap-2">
                  <Textarea 
                    value={editedDescription} 
                    onChange={(e) => setEditedDescription(e.target.value)}
                    placeholder="Enter task description..."
                    className="min-h-[100px] text-sm flex-1"
                  />
                  <div className="flex flex-col gap-2">
                    <Button onClick={handleDescriptionUpdate} size="sm">
                      Save
                    </Button>
                    <Button 
                      onClick={() => {
                        setIsEditingDescription(false);
                        setEditedDescription(task.description || '');
                      }} 
                      variant="outline" 
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  {makeLinksClickable(task.description)}
                </div>
              )}
            </div>}


        <div className="space-y-4 pt-2">


          <Tabs defaultValue="comments" className="w-full">
            <div>
              <TabsList className="flex flex-col sm:flex-row justify-start w-full h-auto bg-transparent p-0 border-0 gap-2 sm:gap-12">
                <TabsTrigger 
                  value="comments" 
                  className="w-full sm:w-auto justify-start sm:justify-center pl-4 sm:pl-0 text-sm bg-transparent border-0 rounded-none sm:rounded-none pb-2 px-0 data-[state=active]:bg-muted sm:data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-0 relative data-[state=active]:after:content-[''] data-[state=active]:after:absolute data-[state=active]:after:left-0 data-[state=active]:after:top-0 data-[state=active]:after:w-0 data-[state=active]:after:h-0 sm:data-[state=active]:after:bottom-0 sm:data-[state=active]:after:top-auto sm:data-[state=active]:after:left-0 sm:data-[state=active]:after:w-full sm:data-[state=active]:after:h-0.5 data-[state=active]:after:bg-yellow-500"
                >
                  Comments
                  {taskComments.length > 0 && (
                    <Badge variant="destructive" className="ml-2 h-5 w-5 text-xs p-0 flex items-center justify-center rounded-full bg-red-500">
                      {taskComments.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger 
                  value="details" 
                  className="w-full sm:w-auto justify-start sm:justify-center pl-4 sm:pl-0 text-sm bg-transparent border-0 rounded-none sm:rounded-none pb-2 px-0 data-[state=active]:bg-muted sm:data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-0 relative data-[state=active]:after:content-[''] data-[state=active]:after:absolute data-[state=active]:after:left-0 data-[state=active]:after:top-0 data-[state=active]:after:w-0 data-[state=active]:after:h-0 sm:data-[state=active]:after:bottom-0 sm:data-[state=active]:after:top-auto sm:data-[state=active]:after:left-0 sm:data-[state=active]:after:w-full sm:data-[state=active]:after:h-0.5 data-[state=active]:after:bg-yellow-500"
                >
                  Details
                </TabsTrigger>
                <TabsTrigger 
                  value="attachment" 
                  className="w-full sm:w-auto justify-start sm:justify-center pl-4 sm:pl-0 text-sm bg-transparent border-0 rounded-none sm:rounded-none pb-2 px-0 data-[state=active]:bg-muted sm:data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-0 relative data-[state=active]:after:content-[''] data-[state=active]:after:absolute data-[state=active]:after:left-0 data-[state=active]:after:top-0 data-[state=active]:after:w-0 data-[state=active]:after:h-0 sm:data-[state=active]:after:bottom-0 sm:data-[state=active]:after:top-auto sm:data-[state=active]:after:left-0 sm:data-[state=active]:after:w-full sm:data-[state=active]:after:h-0.5 data-[state=active]:after:bg-yellow-500"
                >
                  Attachment
                </TabsTrigger>
                <TabsTrigger 
                  value="learnings" 
                  className="w-full sm:w-auto justify-start sm:justify-center pl-4 sm:pl-0 text-sm bg-transparent border-0 rounded-none sm:rounded-none pb-2 px-0 data-[state=active]:bg-muted sm:data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-0 relative data-[state=active]:after:content-[''] data-[state=active]:after:absolute data-[state=active]:after:left-0 data-[state=active]:after:top-0 data-[state=active]:after:w-0 data-[state=active]:after:h-0 sm:data-[state=active]:after:bottom-0 sm:data-[state=active]:after:top-auto sm:data-[state=active]:after:left-0 sm:data-[state=active]:after:w-full sm:data-[state=active]:after:h-0.5 data-[state=active]:after:bg-yellow-500"
                >
                  Learnings
                </TabsTrigger>
                <TabsTrigger 
                  value="watchers" 
                  className="w-full sm:w-auto justify-start sm:justify-center pl-4 sm:pl-0 text-sm bg-transparent border-0 rounded-none sm:rounded-none pb-2 px-0 data-[state=active]:bg-muted sm:data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-0 relative data-[state=active]:after:content-[''] data-[state=active]:after:absolute data-[state=active]:after:left-0 data-[state=active]:after:top-0 data-[state=active]:after:w-0 data-[state=active]:after:h-0 sm:data-[state=active]:after:bottom-0 sm:data-[state=active]:after:top-auto sm:data-[state=active]:after:left-0 sm:data-[state=active]:after:w-full sm:data-[state=active]:after:h-0.5 data-[state=active]:after:bg-yellow-500"
                >
                  Watchers
                  {watchers.length > 0 && (
                    <Badge variant="secondary" className="ml-2 h-5 w-5 text-xs p-0 flex items-center justify-center rounded-full">
                      {watchers.length}
                    </Badge>
                  )}
                </TabsTrigger>
                {taskType === 'foundational' && (
                  <TabsTrigger 
                    value="subtasks" 
                    className="w-full sm:w-auto justify-start sm:justify-center pl-4 sm:pl-0 text-sm bg-transparent border-0 rounded-none sm:rounded-none pb-2 px-0 data-[state=active]:bg-muted sm:data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-0 relative data-[state=active]:after:content-[''] data-[state=active]:after:absolute data-[state=active]:after:left-0 data-[state=active]:after:top-0 data-[state=active]:after:w-0 data-[state=active]:after:h-0 sm:data-[state=active]:after:bottom-0 sm:data-[state=active]:after:top-auto sm:data-[state=active]:after:left-0 sm:data-[state=active]:after:w-full sm:data-[state=active]:after:h-0.5 data-[state=active]:after:bg-yellow-500"
                  >
                    Subtasks
                    {subtasks && subtasks.length > 0 && (
                      <Badge variant="secondary" className="ml-2 h-5 w-5 text-xs p-0 flex items-center justify-center rounded-full">
                        {subtasks.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                )}
                <TabsTrigger 
                  value="time" 
                  className="w-full sm:w-auto justify-start sm:justify-center pl-4 sm:pl-0 text-sm bg-transparent border-0 rounded-none sm:rounded-none pb-2 px-0 data-[state=active]:bg-muted sm:data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-0 relative data-[state=active]:after:content-[''] data-[state=active]:after:absolute data-[state=active]:after:left-0 data-[state=active]:after:top-0 data-[state=active]:after:w-0 data-[state=active]:after:h-0 sm:data-[state=active]:after:bottom-0 sm:data-[state=active]:after:top-auto sm:data-[state=active]:after:left-0 sm:data-[state=active]:after:w-full sm:data-[state=active]:after:h-0.5 data-[state=active]:after:bg-yellow-500"
                >
                  Time
                </TabsTrigger>
              </TabsList>
            </div>
          
          <TabsContent value="comments" className="space-y-4">
            {/* User's Comments Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  User's comments
                </Label>
                <Button variant="ghost" size="sm" onClick={() => setShowCommentsDialog(true)} className="text-xs text-muted-foreground hover:text-foreground">
                  View all ({taskComments.length})
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowAddCommentDialog(true)} 
                  className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  reply
                </Button>
              </div>
            </div>

            {/* Generic Comment Section - Available in all states */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <MessagesSquare className="h-4 w-4" />
                  Generic Comment
                </Label>
              </div>
              {/* Show existing remarks if any */}
              {task.remarks && (
                <div className="text-sm text-muted-foreground bg-muted/50 p-2 rounded-md mb-2">
                  <div className="font-medium mb-1">Current Generic Comment:</div>
                  <div>{makeLinksClickable(task.remarks)}</div>
                </div>
              )}
              <div className="relative">
                <Textarea placeholder="Add generic comment while working on this task..." value={remarks} onChange={e => setRemarks(e.target.value)} className="min-h-[80px] text-sm pr-10 pb-8" />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      onClick={handleRemarksUpdate} 
                      disabled={isUpdatingRemarks || remarks === (task.remarks || '')} 
                      size="sm" 
                      variant="ghost"
                      className="absolute bottom-2 right-2 h-7 w-7 p-0"
                    >
                      {isUpdatingRemarks ? (
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">Save Comment</TooltipContent>
                </Tooltip>
              </div>
            </div>

          </TabsContent>
          
          <TabsContent value="details" className="space-y-4">
            {/* Task Info Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm font-medium">Type</Label>
                <Badge variant="secondary" className="text-xs">
                  {getTaskTypeName(taskType)}
                </Badge>
              </div>
            </div>

            {/* Created, Updated, Source in same row */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span className="font-medium">Created:</span>
                <span>{task.created_at ? format(new Date(task.created_at), 'MMM dd, yyyy') : 'No date'}</span>
              </div>

              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span className="font-medium">Updated:</span>
                <span>{task.updated_at ? format(new Date(task.updated_at), 'MMM dd, yyyy') : 'No date'}</span>
              </div>

              <div className="flex items-center gap-1">
                <span className="font-medium">Source:</span>
                <span className="capitalize">
                  {getSourceTypeName(task.source_type)}
                </span>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="attachment" className="space-y-4">
            {localAttachments && localAttachments.length > 0 ? (
              <div className="grid grid-cols-1 gap-3">
                {localAttachments.map((attachment, index) => {
                  // Determine attachment type and get display info
                  const attachmentType = attachment.type || 'file';
                  const isUrl = attachmentType === 'url';
                  const isText = attachmentType === 'text';
                  const isDocHubTip = attachmentType === 'dochub_tip';
                  const isDocHubFile = attachmentType === 'dochub_file';
                  const isDocHubMom = attachmentType === 'dochub_mom';
                  const isUploadedFile = attachmentType === 'file' || !!attachment.url || !!attachment.path;

                  // Get the URL to open/download
                  const getOpenUrl = () => {
                    if (isUrl) return attachment.value;
                    if (isUploadedFile) return attachment.url || attachment.value;
                    return null;
                  };

                  // Get display name
                  const getDisplayName = () => {
                    if (attachment.name) return attachment.name;
                    if (isUrl) return attachment.value;
                    if (isText) return 'Text Note';
                    if (isDocHubTip) return `Tip: ${attachment.value?.substring(0, 8)}...`;
                    if (isDocHubFile) return `File: ${attachment.value?.substring(0, 8)}...`;
                    if (isDocHubMom) return `MOM: ${attachment.value?.substring(0, 8)}...`;
                    return 'Attachment';
                  };

                  // Get icon based on type
                  const getIcon = () => {
                    if (isUrl) return <Link2 className="h-5 w-5 text-blue-500" />;
                    if (isText) return <FileText className="h-5 w-5 text-green-500" />;
                    if (isDocHubTip) return <Lightbulb className="h-5 w-5 text-primary" />;
                    if (isDocHubFile) return <ClipboardList className="h-5 w-5 text-primary" />;
                    if (isDocHubMom) return <FileText className="h-5 w-5 text-primary" />;
                    return <Paperclip className="h-5 w-5 text-muted-foreground" />;
                  };

                  const openUrl = getOpenUrl();

                  return (
                    <div key={index} className="bg-muted/50 border rounded-lg p-3 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {getIcon()}
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm truncate" title={getDisplayName()}>
                              {getDisplayName()}
                            </p>
                            {isText && attachment.value && (
                              <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                                {attachment.value}
                              </p>
                            )}
                            {attachment.size && (
                              <p className="text-xs text-muted-foreground">
                                {(attachment.size / 1024).toFixed(1)} KB
                              </p>
                            )}
                            {(isDocHubTip || isDocHubFile || isDocHubMom) && (
                              <p className="text-xs text-muted-foreground">
                                ID: {attachment.value}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {/* View button for all types */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (isText) {
                                // Show text note in dialog
                                setDocHubViewContent({ 
                                  title: attachment.name || 'Text Note', 
                                  content: attachment.value || '', 
                                  type: 'tip' 
                                });
                              } else if (isDocHubTip) {
                                handleViewDocHubItem('tip', attachment.value);
                              } else if (isDocHubFile) {
                                handleViewDocHubItem('file', attachment.value);
                              } else if (isDocHubMom) {
                                handleViewDocHubItem('mom', attachment.value);
                              } else if (openUrl) {
                                window.open(openUrl, '_blank');
                              } else if (isUploadedFile && attachment.path) {
                                // Try to get public URL for uploaded file
                                const publicUrl = supabase.storage
                                  .from('task-attachments')
                                  .getPublicUrl(attachment.path).data.publicUrl;
                                if (publicUrl) {
                                  window.open(publicUrl, '_blank');
                                }
                              }
                            }}
                            className="h-8 w-8 p-0"
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {openUrl && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(openUrl, '_blank')}
                              className="h-8 w-8 p-0"
                              title="Download"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAttachmentDelete(index)}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Paperclip className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No attachments</p>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="learnings" className="space-y-4">
            {/* Learnings Section */}
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Learnings
                </Label>
                {/* Show existing comments if any */}
                {task.comments && (
                  <div className="text-sm text-muted-foreground bg-muted/50 p-2 rounded-md mb-2">
                    <div className="font-medium mb-1">Current Learnings:</div>
                    <div>{makeLinksClickable(task.comments)}</div>
                  </div>
                )}
                <div className="relative">
                  <Textarea placeholder="Add comments about this completed task..." value={comments} onChange={e => setComments(e.target.value)} className="min-h-[80px] text-sm pr-10 pb-8" />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        onClick={handleCommentsUpdate} 
                        disabled={isUpdatingComments || comments === (task.comments || '')} 
                        size="sm" 
                        variant="ghost"
                        className="absolute bottom-2 right-2 h-7 w-7 p-0"
                      >
                        {isUpdatingComments ? (
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">Save Learnings</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="watchers" className="space-y-4">
            {/* Watchers Section */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                People to notify (Watchers)
              </Label>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto border rounded-md p-3 bg-background">
                {(taskBrandSquadMembers.length ? taskBrandSquadMembers : squadMembers).map((member) => {
                  const isChecked = watchers.includes(member.id) || (member.user_id && watchers.includes(member.user_id));
                  const handleToggle = (checked: boolean | string) => {
                    const normalizedChecked = checked === true;
                    const existingIds = new Set(watchers);
                    // Remove both possible identifiers
                    existingIds.delete(member.id);
                    if (member.user_id) existingIds.delete(member.user_id);
                    if (normalizedChecked) {
                      // Prefer storing squad member id going forward
                      existingIds.add(member.id);
                    }
                    const newWatchers = Array.from(existingIds);
                    handleWatchersUpdate(newWatchers as string[]);
                  };

                  return (
                    <div key={member.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`watcher-${member.id}`}
                        checked={isChecked}
                        onCheckedChange={handleToggle}
                        disabled={isUpdatingWatchers}
                      />
                      <Label 
                        htmlFor={`watcher-${member.id}`} 
                        className="text-sm cursor-pointer flex-1 truncate"
                        title={`${member.name}${member.alias ? ` (${member.alias})` : ''}`}
                      >
                        <UserHoverCard squadMemberId={member.id} brandId={task.brand_id}>
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                              {member.alias?.slice(0, 2).toUpperCase() || 'U'}
                            </div>
                            <span className="truncate">{member.name}</span>
                          </div>
                        </UserHoverCard>
                      </Label>
                    </div>
                  );
                })}
              </div>
              
              {watchers.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  {watchers.length} watcher{watchers.length > 1 ? 's' : ''} selected - they will receive notifications about task updates
                </div>
              )}
              
              {isUpdatingWatchers && (
                <div className="text-sm text-muted-foreground">
                  Updating watchers...
                </div>
              )}
            </div>
          </TabsContent>

          {taskType === 'foundational' && (
            <TabsContent value="subtasks" className="space-y-4">
              {task && task.brand_id && (
                <SubtasksTab 
                  parentTaskId={task.id} 
                  brandId={task.brand_id}
                  task={task}
                  onSubtaskClick={(subtaskId) => {
                    onRefetch?.();
                  }}
                />
              )}
            </TabsContent>
          )}

          <TabsContent value="time" className="space-y-4">
            {task && task.brand_id && (
              <TimeHistoryTab 
                taskId={task.id}
                taskType={taskType as 'foundational' | 'growth' | 'recurring'}
                brandId={task.brand_id}
              />
            )}
          </TabsContent>
        </Tabs>

              {/* Time Tracker - Show when task is in_progress or done - Full width grey area with top border */}
              {(currentStatus === 'done' || currentStatus === 'in_progress') && (
          <div className="bg-muted/50 -mx-4 sm:-mx-6 -mb-6 px-4 sm:px-6 py-3 border-t border-border">
            <div className="flex items-center justify-between">
              <TimeTracker 
                value={timeSpent} 
                onChange={(minutes, source) => {
                  setTimeSpent(minutes);
                  setTimeSource(source);
                }}
                allowManualInput={taskCreationSettings?.allow_manual_time_adjustment ?? true}
              />
              <Button onClick={handleTimeSpentUpdate} disabled={isUpdatingTimeSpent} size="sm" variant="outline" className="h-9 min-w-[44px] px-3">
                <Save className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{isUpdatingTimeSpent ? 'Saving...' : 'Save Time'}</span>
              </Button>
            </div>
          </div>
        )}

        </div>
        </div>
      </DialogContent>

      {/* Comments Dialog */}
      {task && <CommentsDialog isOpen={showCommentsDialog} onClose={() => setShowCommentsDialog(false)} taskId={task.id} taskType={taskType} taskBrandId={task.brand_id} onAddComment={() => {
      setShowCommentsDialog(false);
      setShowAddCommentDialog(true);
    }} />}

      {/* Add Comment Dialog */}
      {task && <AddCommentDialog isOpen={showAddCommentDialog} onClose={() => setShowAddCommentDialog(false)} taskId={task.id} taskType={taskType} taskBrandId={task.brand_id} onSuccess={() => {
      // Comments will be automatically refreshed via the hook
    }} />}

      {/* Image Viewer Dialog */}
      <Dialog open={showImageViewer} onOpenChange={setShowImageViewer}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Task Images ({localImages?.length || 0})</DialogTitle>
          </DialogHeader>
          
          {localImages && localImages.length > 0 ? <ImageCarousel images={localImages} onDelete={handleImageDelete} /> : <div className="flex items-center justify-center h-64">
              <p className="text-center text-muted-foreground">No images available</p>
            </div>}
        </DialogContent>
      </Dialog>

      {/* Tags Management Dialog */}
      {taskType === 'foundational' && task && task.brand_id && (
        <Dialog open={showTagsDialog} onOpenChange={setShowTagsDialog}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Manage Tags</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <TagSelector 
                brandId={task.brand_id}
                selectedTagIds={tagIds}
                onTagsChange={async (newTagIds) => {
                  const previousTagIds = tagIds;
                  setTagIds(newTagIds);
                  updateSelectedTask({ tag_ids: newTagIds as any });
                  
                  try {
                    const { error } = await supabase
                      .from('foundational_tasks')
                      .update({ tag_ids: newTagIds })
                      .eq('id', task.id);

                    if (error) {
                      console.error('Error updating tags:', error);
                      toast({
                        title: "Error",
                        description: "Failed to save tags",
                        variant: "destructive",
                      });
                      setTagIds(previousTagIds);
                      updateSelectedTask({ tag_ids: previousTagIds as any });
                    } else {
                      onRefetch?.();
                      toast({
                        title: "Success",
                        description: "Tags updated successfully",
                      });
                    }
                  } catch (err) {
                    console.error('Unexpected error updating tags:', err);
                    toast({
                      title: "Error",
                      description: "Failed to save tags",
                      variant: "destructive",
                    });
                    setTagIds(previousTagIds);
                    updateSelectedTask({ tag_ids: previousTagIds as any });
                  }
                }}
              />
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setShowTagsDialog(false)} variant="outline">
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* DocHub Content View Dialog */}
      {docHubViewContent && (
        <Dialog open={!!docHubViewContent} onOpenChange={() => setDocHubViewContent(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {docHubViewContent.type === 'tip' && <Lightbulb className="h-5 w-5 text-primary" />}
                {docHubViewContent.type === 'file' && <ClipboardList className="h-5 w-5 text-primary" />}
                {docHubViewContent.type === 'mom' && <FileText className="h-5 w-5 text-primary" />}
                {docHubViewContent.title}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4 whitespace-pre-wrap text-sm">
              {docHubViewContent.content}
            </div>
            <div className="flex justify-end mt-4">
              <Button variant="outline" onClick={() => setDocHubViewContent(null)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>;
};